import React, { useState, useEffect } from 'react';
import { Sale } from '../db';
import { salesAPI } from '../backend';
import { CreditCard, Clock, RotateCcw } from 'lucide-react';
import { formatPhone } from '../utils/formatPhone';
import PaymentDialog from './PaymentDialog';
import ReturnDialog from './ReturnDialog';
import { brandsOf, NO_BRAND } from '../utils/brands';

function Sales() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterQuick, setFilterQuick] = useState('all');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [sales, setSales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSale, setSelectedSale] = useState<any>(null);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);
  const [duplicateIds, setDuplicateIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const salesData = await salesAPI.getAll();
      setSales(salesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  // Recompute duplicates when sales change
  useEffect(() => {
    const ids = computeDuplicateIds(sales);
    setDuplicateIds(ids);
  }, [sales]);

  const handleStatusChange = async (saleId: number, newStatus: string) => {
    try {
      await salesAPI.update(saleId, { payment_status: newStatus });
      
      // Update the sale locally without refreshing the page
      setSales(prevSales => 
        prevSales.map(sale => 
          sale.id === saleId 
            ? { ...sale, payment_status: newStatus }
            : sale
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating sale');
      // Optionally revert the change on error by refreshing data
      await loadData();
    }
  };

  const handleAbonoClick = (sale: any) => {
    setSelectedSale(sale);
    setIsPaymentDialogOpen(true);
  };

  const handleReturnClick = (sale: any) => {
    setSelectedSale(sale);
    setIsReturnDialogOpen(true);
  };

  const handlePaymentComplete = () => {
    setIsPaymentDialogOpen(false);
    setSelectedSale(null);
    loadData(); // Refresh sales data to show updated payment status
  };

  const handlePaymentDialogClose = () => {
    setIsPaymentDialogOpen(false);
    setSelectedSale(null);
  };

  const handleReturnDialogClose = () => {
    setIsReturnDialogOpen(false);
    setSelectedSale(null);
  };

  const handleReturnConfirm = (_selection: any) => {
    // After processing returns in dialog, refresh sales to reflect changes
    setIsReturnDialogOpen(false);
    setSelectedSale(null);
    loadData();
  };

  // Helper function to calculate payment progress for a sale
  const calculateSaleProgress = (sale: any) => {
    if (!sale.sale_products || sale.sale_products.length === 0) {
      return { percentage: 0, totalPaid: 0, totalAmount: sale.total, paidProducts: 0, totalProducts: 0 };
    }

    let totalPaid = 0;
    let totalAmount = 0;
    let paidProducts = 0;
    let totalProducts = sale.sale_products.length;

    sale.sale_products.forEach((sp: any) => {
      const productTotal = sp.price * sp.quantity;
      const productPaid = sp.price * (sp.quantity_paid || 0);
      totalPaid += productPaid;
      totalAmount += productTotal;
      
      if ((sp.quantity_paid || 0) >= sp.quantity) {
        paidProducts++;
      }
    });

    return {
      percentage: totalAmount > 0 ? (totalPaid / totalAmount) * 100 : 0,
      totalPaid,
      totalAmount,
      paidProducts,
      totalProducts
    };
  };

  // Build a stable signature of a sale for duplicate detection
  const computeDuplicateIds = (allSales: any[]): Set<number> => {
    const signatureToIds: Record<string, number[]> = {};

    const getDateHourKey = (dateString: string) => {
      try {
        const d = new Date(dateString);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const h = String(d.getHours()).padStart(2, '0');
        return `${y}-${m}-${day}T${h}`;
      } catch (_) {
        return dateString;
      }
    };

    const getClientKey = (client: any) => {
      if (!client) return 'no-client';
      return client.id ?? `${client.name || ''}|${client.email || ''}|${client.phone || ''}`;
    };

    const getProductsKey = (saleProducts: any[] = []) => {
      const parts = saleProducts.map((sp: any) => {
        const pid = sp.product?.id ?? sp.product_id ?? sp.product?.name ?? 'unknown';
        const qty = sp.quantity ?? 0;
        const price = typeof sp.price === 'number' ? sp.price.toFixed(2) : String(sp.price);
        return `${pid}|${qty}|${price}`;
      });
      parts.sort();
      return parts.join(';');
    };

    for (const s of allSales) {
      const clientKey = getClientKey(s.client);
      const totalKey = typeof s.total === 'number' ? s.total.toFixed(2) : String(s.total);
      const productsKey = getProductsKey(s.sale_products || []);
      const dateHourKey = getDateHourKey(s.created_at || s.date);
      const signature = `${clientKey}__${totalKey}__${productsKey}__${dateHourKey}`;
      if (!signatureToIds[signature]) signatureToIds[signature] = [];
      signatureToIds[signature].push(s.id);
    }

    const duplicates = new Set<number>();
    Object.values(signatureToIds).forEach(ids => {
      if (ids.length > 1) ids.forEach(id => duplicates.add(id));
    });
    return duplicates;
  };

  // Helper function to format date and time
  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      // Fallback for old date format (date only)
      return dateString;
    }
  };

  const availableYears = Array.from(new Set(sales.map(s => {
    try { return new Date(s.created_at).getFullYear(); } catch { return null; }
  }).filter(Boolean) as number[])).sort((a, b) => b - a);

  const filteredSales = sales.filter(sale => {
    const client = sale.client;
    const saleProducts = sale.sale_products || [];

    const matchesSearch =
      (client && (client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase()))) ||
      saleProducts.some((saleProduct: any) =>
        (saleProduct.product?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (saleProduct.product?.brand || '').toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesStatus = filterStatus === 'all'
      || (filterStatus === 'Muestra' ? sale.sale_type === 'Muestra' : sale.payment_status === filterStatus);

    // A sale matches a brand when any of its lines carries that brand
    const matchesBrand = filterBrand === ''
      || saleProducts.some((saleProduct: any) =>
        filterBrand === NO_BRAND ? !saleProduct.product?.brand : saleProduct.product?.brand === filterBrand
      );

    let matchesDate = true;
    const saleDate = new Date(sale.created_at);
    const now = new Date();
    if (filterQuick === 'last7') {
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - 7);
      matchesDate = saleDate >= cutoff;
    } else if (filterQuick === 'last28') {
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - 28);
      matchesDate = saleDate >= cutoff;
    } else {
      if (filterYear) {
        matchesDate = matchesDate && saleDate.getFullYear() === Number(filterYear);
      }
      if (filterMonth) {
        matchesDate = matchesDate && saleDate.getMonth() + 1 === Number(filterMonth);
      }
    }

    return matchesSearch && matchesStatus && matchesBrand && matchesDate;
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando ventas...</p>
      </div>
    );
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pagado': return 'status-delivered';
      case 'Por pagar': return 'status-processing';
      case 'Devuelto': return 'status-returned';
      default: return 'status-default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Pagado': return <CreditCard size={16} />;
      case 'Por pagar': return <Clock size={16} />;
      case 'Devuelto': return <RotateCcw size={16} />;
      default: return null;
    }
  };

  const totalSales = sales.length;
  const pendingPayments = sales.filter(sale => sale.payment_status === 'Por pagar').length;

  // Brands present anywhere in the sales history, for the brand filter
  const brandOptions = brandsOf(
    sales.flatMap((sale: any) => (sale.sale_products || []).map((sp: any) => ({ brand: sp.product?.brand })))
  );

  return (
    <div className="main-view">
      <div className="sales-header">
        <h1>Ventas</h1>
        <p>Gestiona y revisa el historial de ventas</p>
      </div>

      <div className="search-section">
        <input
          type="text"
          placeholder="Buscar por cliente, producto o marca..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="status-filter"
        >
          <option value="all">Todos los estados</option>
          <option value="Pagado">Pagado</option>
          <option value="Por pagar">Por pagar</option>
          <option value="Devuelto">Devuelto</option>
          <option value="Muestra">Muestras</option>
        </select>
        {brandOptions.length > 0 && (
          <select
            value={filterBrand}
            onChange={(e) => setFilterBrand(e.target.value)}
            className="status-filter"
          >
            <option value="">Todas las marcas</option>
            {brandOptions.map(b => <option key={b} value={b}>{b}</option>)}
            <option value={NO_BRAND}>{NO_BRAND}</option>
          </select>
        )}
        <select
          value={filterQuick}
          onChange={(e) => { setFilterQuick(e.target.value); setFilterMonth(''); setFilterYear(''); }}
          className="status-filter"
        >
          <option value="all">Todas las fechas</option>
          <option value="last7">Últimos 7 días</option>
          <option value="last28">Últimos 28 días</option>
        </select>
        <select
          value={filterMonth}
          onChange={(e) => { setFilterMonth(e.target.value); setFilterQuick('all'); }}
          className="status-filter"
          disabled={filterQuick !== 'all'}
        >
          <option value="">Todos los meses</option>
          <option value="1">Enero</option>
          <option value="2">Febrero</option>
          <option value="3">Marzo</option>
          <option value="4">Abril</option>
          <option value="5">Mayo</option>
          <option value="6">Junio</option>
          <option value="7">Julio</option>
          <option value="8">Agosto</option>
          <option value="9">Septiembre</option>
          <option value="10">Octubre</option>
          <option value="11">Noviembre</option>
          <option value="12">Diciembre</option>
        </select>
        <select
          value={filterYear}
          onChange={(e) => { setFilterYear(e.target.value); setFilterQuick('all'); }}
          className="status-filter"
          disabled={filterQuick !== 'all'}
        >
          <option value="">Todos los años</option>
          {availableYears.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      <p className="sales-results-count">
        {filteredSales.length === totalSales
          ? `${totalSales} ventas`
          : `${filteredSales.length} de ${totalSales} ventas`}
      </p>

      <div className="sales-list">
        {filteredSales.map(sale => {
          const client = sale.client;
          const progress = calculateSaleProgress(sale);
          const isSample = sale.sale_type === 'Muestra';
          const isReturned = sale.payment_status === 'Devuelto';
          return (
            <div key={sale.id} className="sale-card">
              <div className="sale-header">
                <div className="sale-info">
                  <h3>Venta #{sale.id}</h3>
                  <p className="sale-date">{formatDateTime(sale.created_at)}</p>
                </div>
                <div className="sale-header-badges">
                  {isSample && (
                    <span className="badge-sample">🎁 Muestra</span>
                  )}
                  {isReturned && (
                    <span className="badge-returned">↩️ Devuelto</span>
                  )}
                  {duplicateIds.has(sale.id) && (
                    <span className="badge-duplicate">Duplicado</span>
                  )}
                </div>
              </div>
              <div className="sale-customer">
                {client ? (
                  <>
                    <p className="client-item name">{client.name}</p>
                    <p className="client-item">{client.email}</p>
                    <p className="client-item">{formatPhone(client.phone)}</p>
                  </>
                ) : (
                  <p style={{ color: 'red' }}>Cliente no encontrado</p>
                )}
              </div>
              <div className="sale-products">
                <h4>Productos ({(sale.sale_products || []).reduce((total: number, sp: any) => total + sp.quantity, 0)})</h4>
                {(sale.sale_products || []).map((saleProduct: any, index: number) => (
                  <div key={index} className="sale-product">
                    <span>
                      {saleProduct.product?.name}
                      {saleProduct.product?.brand && <span className="sale-product-brand"> · {saleProduct.product.brand}</span>}
                      {' '}x{saleProduct.quantity}
                    </span>
                    <span>{isSample ? 'GRATIS' : `$${(saleProduct.price * saleProduct.quantity).toFixed(2)}`}</span>
                  </div>
                ))}
              </div>

              {/* Payment Progress */}
              {!isSample && !isReturned && progress.totalProducts > 0 && (
                <div className="sale-payment-progress">
                  <div className="progress-header">
                    <span>Progreso de Pago</span>
                    <span className="progress-percentage">{progress.percentage.toFixed(1)}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progress.percentage}%` }}
                    ></div>
                  </div>
                  <div className="progress-summary">
                    <span>${progress.totalPaid.toFixed(2)} de ${progress.totalAmount.toFixed(2)}</span>
                    <span>{progress.paidProducts} de {progress.totalProducts} productos</span>
                  </div>
                </div>
              )}

              <div className="sale-footer">
                <div className="sale-delivery">
                  <span>Entrega: {sale.delivery_method}</span>
                </div>
                <div className="sale-total">
                  <span>Total: ${sale.total.toFixed(2)}</span>
                </div>
              </div>
              <div className="sale-actions">
                  {isSample ? (
                    <div className="sale-status-section">
                      <span className="status-select-static badge-sample">🎁 Muestra</span>
                    </div>
                  ) : isReturned ? (
                    <div className="sale-status-section">
                      <span className="status-select-static badge-returned">↩️ Devuelto</span>
                    </div>
                  ) : (
                    <div className="sale-status-section">
                      <div className="status-icon">
                        {getStatusIcon(sale.payment_status)}
                      </div>
                      <select
                        value={sale.payment_status}
                        onChange={e => handleStatusChange(sale.id, e.target.value)}
                        className={`status-select ${getStatusColor(sale.payment_status)}`}
                      >
                        <option value="Por pagar">Por pagar</option>
                        <option value="Pagado">Pagado</option>
                      </select>
                    </div>
                  )}
                  {!isSample && !isReturned && (
                    <button className="btn-secondary" onClick={() => handleAbonoClick(sale)}>
                      Abono
                    </button>
                  )}
                  {!isReturned && (
                    <button className="btn-secondary" onClick={() => handleReturnClick(sale)}>
                      Devolución
                    </button>
                  )}
                </div>

            </div>
          );
        })}
        {filteredSales.length === 0 && (
          <div className="no-sales">
            <p>No se encontraron ventas que coincidan con los filtros.</p>
          </div>
        )}
      </div>
      {selectedSale && (
        <PaymentDialog
          isOpen={isPaymentDialogOpen}
          onClose={handlePaymentDialogClose}
          onPaymentComplete={handlePaymentComplete}
          sale={selectedSale}
        />
      )}
      {selectedSale && (
        <ReturnDialog
          isOpen={isReturnDialogOpen}
          onClose={handleReturnDialogClose}
          onConfirm={handleReturnConfirm}
          sale={selectedSale}
        />
      )}
    </div>
  );
}

export default Sales; 